/**
 * Move 1 Week 3 — Atomic plan executor.
 *
 * Takes a declarative build plan (JSON array of operations) and executes
 * the whole thing inside ONE Excel.run. Replaces the current pattern of
 * 4-6 sequential agent tool calls with a single one. The agent emits the
 * plan, frontend runs it, returns a summary.
 *
 * Why this matters for cost / speed:
 * - One LLM round trip instead of 4-6 (saves ~25-40 seconds wall time)
 * - One Excel.run sync instead of 4-6 (saves ~1-2 seconds + atomic state)
 * - Agent reasons about the WHOLE dashboard at once, not piecewise
 *
 * Plan operations are declarative. Each op resolves to a small set of
 * Office.js calls inside the shared context.
 *
 * If one op fails, the executor aborts the whole plan and returns the
 * index of the failing op + the error. Excel.run automatically rolls back
 * unsynced changes, so partial damage to the workbook is bounded.
 */

type CellValue = string | number | boolean | null;

export type ChartType =
  | 'ColumnClustered'
  | 'ColumnStacked'
  | 'ColumnStacked100'
  | 'BarClustered'
  | 'BarStacked'
  | 'BarStacked100'
  | 'Line'
  | 'LineMarkers'
  | 'Pie'
  | 'Doughnut'
  | 'Area'
  | 'AreaStacked'
  | 'XYScatter'
  | 'Waterfall';

export type SparklineType = 'Line' | 'Column' | 'Stacked';

export type PlanOp =
  | { op: 'create_sheet'; name: string; activate?: boolean }
  | { op: 'write_values'; sheet: string; startCell: string; values: CellValue[][]; numberFormats?: (string | null)[][]; boldFirstRow?: boolean; autoFitColumns?: boolean }
  | { op: 'write_formulas_template'; sheet: string; startCell: string; rows: number; cols: number; template: string }
  | { op: 'format_section'; sheet: string; range: string; fill?: string; fontColor?: string; fontBold?: boolean; fontItalic?: boolean; fontName?: string; fontSize?: number; horizontalAlignment?: 'Left' | 'Center' | 'Right'; verticalAlignment?: 'Top' | 'Center' | 'Bottom'; wrapText?: boolean; numberFormat?: string; merge?: boolean; rowHeight?: number; bordersBottom?: boolean; bordersTop?: boolean; bordersLeft?: boolean; bordersRight?: boolean; bordersAll?: boolean; borderColor?: string }
  | { op: 'conditional_format'; sheet: string; range: string; type: 'colorScale' | 'cellValue' | 'dataBar' | 'iconSet'; rule?: { operator?: 'greaterThan' | 'lessThan' | 'equal' | 'between'; value?: number | string; secondValue?: number | string; fontColor?: string; fill?: string; bold?: boolean }; minColor?: string; midColor?: string; maxColor?: string; barColor?: string; iconStyle?: 'ThreeArrows' | 'ThreeTrafficLights1' | 'ThreeSymbols' | 'FiveArrows' }
  | { op: 'add_chart'; sheet: string; chartType: ChartType; sourceRange: string; topLeft: string; widthColumns: number; heightRows: number; title?: string; seriesNames?: string[]; seriesOverrides?: { index: number; chartType?: ChartType; axisGroup?: 'Primary' | 'Secondary'; color?: string }[]; hideLegend?: boolean; hideAxes?: boolean; doughnutHoleSize?: number }
  | { op: 'add_sparkline'; sheet: string; dataRange: string; locationRange: string; sparklineType: SparklineType; lineColor?: string }
  | { op: 'add_named_range'; name: string; refersTo: string; comment?: string }
  | { op: 'add_table'; sheet: string; range: string; name: string; hasHeaders?: boolean; style?: string }
  | { op: 'add_slicer'; sheet: string; tableName: string; field: string; topLeft: string; widthColumns: number; heightRows: number; caption?: string }
  | { op: 'add_text_box'; sheet: string; topLeft: string; widthColumns: number; heightRows: number; text: string; fill?: string; fontColor?: string; fontBold?: boolean; fontSize?: number; horizontalAlignment?: 'Left' | 'Center' | 'Right'; verticalAlignment?: 'Top' | 'Center' | 'Bottom' }
  | { op: 'freeze_panes'; sheet: string; rows?: number; columns?: number }
  | { op: 'set_column_widths'; sheet: string; widths: { col: string | number; width: number }[] }
  | { op: 'set_row_heights'; sheet: string; heights: { row: number; height: number }[] }
  | { op: 'data_validation_list'; sheet: string; range: string; source: string }
  | { op: 'activate_sheet'; sheet: string }
  | { op: 'add_page_break'; sheet: string; row: number }
  | {
      op: 'set_print_area';
      sheet: string;
      range: string;
      orientation?: 'Landscape' | 'Portrait';
      /** Fit-to-page zoom — common values: 1 (one page wide x N pages tall), or 'fitAll'. */
      fitToPagesWide?: number;
      fitToPagesTall?: number;
      /** Centre the print on the page horizontally. */
      centerHorizontally?: boolean;
    };

/**
 * Move 4 Sprint 1 — structured errors and post-render validation.
 *
 * The May 2026 production failure had the agent abandoning apply_workbook_plan
 * after a vague error and falling into a 40-call legacy-tool spiral. The error
 * shape was `[op 3 write_values] something` — no op_index, no error code, no
 * payload. The agent had no way to fix the specific op and self-correct.
 *
 * Now the executor returns:
 *   - structured `error` with op_index, op_type, error_code, op_payload, message
 *   - structured `validation` report with passed bool + issues array
 *
 * The agent reads these as tool_result content and can fix or retry. A claim
 * of "Build Complete" without `validation.passed === true` is forbidden by the
 * system prompt (Move 4 Sprint 1.6).
 */
export type ErrorCode =
  | 'sheet_not_found'
  | 'invalid_range'
  | 'invalid_width'
  | 'empty_values'
  | 'invalid_chart_type'
  | 'office_api_error'
  | 'unknown_op_type'
  | 'plan_too_large'
  | 'unknown';

export interface OfficeDebugInfo {
  /** Office.js error code (e.g. "InvalidArgument", "ItemNotFound"). */
  code?: string;
  /** Office.js error message — usually more specific than .message. */
  errorMessage?: string;
  /** Where in the API the error occurred (e.g. "Range.format.borders.getItem"). */
  errorLocation?: string;
  /** The full statement that triggered the error, when Office.js provides it. */
  fullStatement?: string;
}

export interface PlanExecutionError {
  op_index: number;
  op_type: string;
  op_payload: PlanOp;
  error_code: ErrorCode;
  message: string;
  /**
   * Move 4 hotfix #6 — Office.js error details extracted from
   * (e as OfficeExtension.Error).debugInfo. Surfaces the exact API call
   * that failed instead of just a generic JS error message.
   */
  office_debug?: OfficeDebugInfo;
}

export interface ValidationIssue {
  type:
    | 'truncated_cells'
    | 'missing_chart'
    | 'empty_merged_cell'
    | 'formula_error_cells'        // Move 5 Step 3 — #REF!/#NAME?/#DIV/0!/etc rendered in a written cell
    | 'named_range_missing'        // Move 5 Step 3 — add_named_range op didn't produce a usable name
    | 'unknown_validation_error';
  range?: string;
  sheet?: string;
  message: string;
  auto_fixed: boolean;
  /** Move 5 Step 3 — for formula_error_cells, the actual error token Excel rendered. */
  errorToken?: string;
}

export interface ValidationReport {
  passed: boolean;
  issues: ValidationIssue[];
  /** Number of cells scanned for ### truncation. */
  cells_scanned: number;
  /** Columns that were auto-widened during validation. */
  columns_widened: number;
  /** Number of charts found vs expected (per add_chart op count). */
  charts_expected: number;
  charts_found: number;
  /** Move 5 Step 3 — count of named ranges expected (per add_named_range op) and actually found. */
  named_ranges_expected?: number;
  named_ranges_found?: number;
  /** Move 5 Step 3 — count of cells found rendering a formula-error token. */
  formula_errors_found?: number;
}

export interface PlanExecutionResult {
  ok: boolean;
  opsCompleted: number;
  opsTotal: number;
  durationMs: number;
  /** Structured error if execution failed. Always set when ok=false. */
  error?: PlanExecutionError;
  /** Post-render validation report. Always set when ok=true. */
  validation?: ValidationReport;
}

const COL_WIDTH_MIN = 10;
const COL_WIDTH_MAX = 500;

function colNumToLetter(c: number): string {
  let s = '';
  while (c > 0) {
    const r = (c - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    c = Math.floor((c - 1) / 26);
  }
  return s;
}

function colSpecToIndex1(col: string | number): number {
  if (typeof col === 'number') return Math.max(1, col);
  const trimmed = col.trim().toUpperCase();
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
  let n = 0;
  for (const ch of trimmed) n = n * 26 + (ch.charCodeAt(0) - 64);
  return Math.max(1, n);
}

/** Get a worksheet by name, optionally creating it if missing. */
async function getSheet(ctx: Excel.RequestContext, name: string, createIfMissing: boolean): Promise<Excel.Worksheet | null> {
  const ws = ctx.workbook.worksheets.getItemOrNullObject(name);
  ws.load('name');
  await ctx.sync();
  if ((ws as unknown as { isNullObject?: boolean }).isNullObject) {
    if (createIfMissing) {
      const created = ctx.workbook.worksheets.add(name);
      return created;
    }
    return null;
  }
  return ws;
}

function expandTemplate(template: string, row: number, col: number, i: number, j: number): string {
  return template
    .replace(/\{row\}/g, String(row))
    .replace(/\{col\}/g, colNumToLetter(col))
    .replace(/\{i\}/g, String(i))
    .replace(/\{j\}/g, String(j));
}

async function execOp(ctx: Excel.RequestContext, op: PlanOp): Promise<void> {
  switch (op.op) {
    case 'create_sheet': {
      // Move 4 hotfix #4 — make this fully idempotent across re-runs.
      //
      // The previous behaviour (clear used range only) left stale charts,
      // shapes, sparkline groups, and sheet-scoped named ranges from prior
      // runs. New plan ops (add_chart, add_sparkline, add_named_range,
      // add_table) collide with leftovers and the executePlan loop's outer
      // sync throws, with no clean way to attribute the failure to a
      // specific op.
      //
      // Delete-and-re-add wipes ALL sheet-level artifacts in one shot.
      // Switch active sheet to another sheet first if the target is the
      // current active sheet (Office.js can fail to delete the active
      // sheet on some hosts).
      const existing = ctx.workbook.worksheets.getItemOrNullObject(op.name);
      existing.load(['name', 'visibility']);
      await ctx.sync();
      if (!(existing as unknown as { isNullObject?: boolean }).isNullObject) {
        // Switch active sheet away from the one being deleted.
        const all = ctx.workbook.worksheets;
        all.load('items/name');
        await ctx.sync();
        const others = all.items.filter(w => w.name !== op.name);
        if (others.length > 0) {
          others[0].activate();
          await ctx.sync();
        }
        existing.delete();
        await ctx.sync();
      }
      const target = ctx.workbook.worksheets.add(op.name);
      if (op.activate !== false) target.activate();
      return;
    }

    case 'write_values': {
      const ws = await getSheet(ctx, op.sheet, true);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      const start = ws.getRange(op.startCell);
      const rows = op.values.length;
      if (rows === 0) return;
      const cols = op.values[0].length;
      const range = start.getResizedRange(rows - 1, cols - 1);
      // Move 4 hotfix #6 — apply numberFormat BEFORE values when provided.
      // Excel auto-coerces date-like strings ("Jul-25", "25-Jul", "1/7/2025")
      // to Date objects when written into a cell with default (General) format.
      // For period-selector dropdowns and helper-label ranges where we need
      // labels to remain LITERAL TEXT (so MATCH against helper labels works),
      // the caller passes numberFormats: [['@'], ...]. Setting format first
      // tells Excel to store the value as text without coercion.
      if (op.numberFormats) {
        range.numberFormat = op.numberFormats as Excel.Range['numberFormat'];
      }
      range.values = op.values as Excel.Range['values'];
      if (op.boldFirstRow && rows > 0) {
        const firstRow = start.getResizedRange(0, cols - 1);
        firstRow.format.font.bold = true;
      }
      if (op.autoFitColumns) {
        range.format.autofitColumns();
      }
      return;
    }

    case 'write_formulas_template': {
      const ws = await getSheet(ctx, op.sheet, true);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      // Resolve start cell to row+col
      const start = ws.getRange(op.startCell);
      start.load(['rowIndex', 'columnIndex']);
      await ctx.sync();
      const startRow = start.rowIndex + 1;
      const startCol = start.columnIndex + 1;
      const grid: string[][] = [];
      for (let i = 0; i < op.rows; i++) {
        const row: string[] = [];
        for (let j = 0; j < op.cols; j++) {
          row.push(expandTemplate(op.template, startRow + i, startCol + j, i, j));
        }
        grid.push(row);
      }
      const range = start.getResizedRange(op.rows - 1, op.cols - 1);
      range.formulas = grid;
      return;
    }

    case 'format_section': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      const range = ws.getRange(op.range);
      if (op.merge) range.merge();
      if (op.fill) range.format.fill.color = op.fill;
      if (op.fontColor) range.format.font.color = op.fontColor;
      if (op.fontBold !== undefined) range.format.font.bold = op.fontBold;
      if (op.fontItalic !== undefined) range.format.font.italic = op.fontItalic;
      if (op.fontName) range.format.font.name = op.fontName;
      if (op.fontSize !== undefined) range.format.font.size = op.fontSize;
      if (op.horizontalAlignment) range.format.horizontalAlignment = op.horizontalAlignment;
      if (op.verticalAlignment) range.format.verticalAlignment = op.verticalAlignment;
      if (op.wrapText !== undefined) range.format.wrapText = op.wrapText;
      if (op.numberFormat) {
        // Apply uniform number format across the range
        range.load(['rowCount', 'columnCount']);
        await ctx.sync();
        const grid: string[][] = [];
        for (let r = 0; r < range.rowCount; r++) {
          const row: string[] = [];
          for (let c = 0; c < range.columnCount; c++) row.push(op.numberFormat);
          grid.push(row);
        }
        range.numberFormat = grid;
      }
      if (op.rowHeight !== undefined) {
        range.format.rowHeight = op.rowHeight;
      }
      const borderColor = op.borderColor;
      const setBorder = (edge: 'EdgeTop' | 'EdgeBottom' | 'EdgeLeft' | 'EdgeRight' | 'InsideHorizontal' | 'InsideVertical') => {
        const b = range.format.borders.getItem(edge);
        b.style = 'Continuous';
        if (borderColor) b.color = borderColor;
      };
      if (op.bordersAll) {
        (['EdgeTop', 'EdgeBottom', 'EdgeLeft', 'EdgeRight', 'InsideHorizontal', 'InsideVertical'] as const).forEach(setBorder);
      } else {
        if (op.bordersTop) setBorder('EdgeTop');
        if (op.bordersBottom) setBorder('EdgeBottom');
        if (op.bordersLeft) setBorder('EdgeLeft');
        if (op.bordersRight) setBorder('EdgeRight');
      }
      return;
    }

    case 'conditional_format': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      const range = ws.getRange(op.range);
      if (op.type === 'colorScale') {
        const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.colorScale);
        cf.colorScale.criteria = {
          minimum: { type: Excel.ConditionalFormatColorCriterionType.lowestValue, color: op.minColor || '#F8696B' },
          midpoint: { type: Excel.ConditionalFormatColorCriterionType.percentile, formula: '50', color: op.midColor || '#FFEB84' },
          maximum: { type: Excel.ConditionalFormatColorCriterionType.highestValue, color: op.maxColor || '#63BE7B' },
        };
      } else if (op.type === 'iconSet') {
        const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.iconSet);
        const styleMap: Record<string, Excel.IconSet> = {
          ThreeArrows: Excel.IconSet.threeArrows,
          ThreeTrafficLights1: Excel.IconSet.threeTrafficLights1,
          ThreeSymbols: Excel.IconSet.threeSymbols,
          FiveArrows: Excel.IconSet.fiveArrows,
        };
        cf.iconSet.style = styleMap[op.iconStyle ?? 'ThreeArrows'] ?? Excel.IconSet.threeArrows;
      } else if (op.type === 'dataBar') {
        const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.dataBar);
        cf.dataBar.barDirection = Excel.ConditionalDataBarDirection.leftToRight;
        if (op.barColor) cf.dataBar.positiveFormat.fillColor = op.barColor;
      } else if (op.type === 'cellValue') {
        const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.cellValue);
        const operator = (op.rule?.operator ?? 'greaterThan');
        const opMap: Record<string, Excel.ConditionalCellValueOperator> = {
          greaterThan: Excel.ConditionalCellValueOperator.greaterThan,
          lessThan: Excel.ConditionalCellValueOperator.lessThan,
          equal: Excel.ConditionalCellValueOperator.equalTo,
          between: Excel.ConditionalCellValueOperator.between,
        };
        cf.cellValue.rule = {
          formula1: String(op.rule?.value ?? 0),
          ...(op.rule?.secondValue !== undefined ? { formula2: String(op.rule.secondValue) } : {}),
          operator: opMap[operator] ?? Excel.ConditionalCellValueOperator.greaterThan,
        };
        if (op.rule?.fontColor) cf.cellValue.format.font.color = op.rule.fontColor;
        if (op.rule?.fill) cf.cellValue.format.fill.color = op.rule.fill;
        if (op.rule?.bold) cf.cellValue.format.font.bold = true;
      }
      return;
    }

    case 'add_chart': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      // Create the chart inside a localized try+sync so an unsupported chartType
      // or bad range (e.g. Waterfall on an older Office host) skips just this
      // chart instead of aborting the whole plan at the per-op sync.
      let chart: Excel.Chart | undefined;
      try {
        const data = ws.getRange(op.sourceRange);
        chart = ws.charts.add(op.chartType as Excel.ChartType, data, Excel.ChartSeriesBy.auto);
        await ctx.sync();
      } catch (e) {
        console.warn(`[planExecutor] add_chart skipped (chartType=${op.chartType}, range=${op.sourceRange}): ${(e as Error)?.message ?? e}`);
        return;
      }
      if (!chart) return;
      // Position
      const tl = ws.getRange(op.topLeft);
      tl.load(['left', 'top', 'width', 'height']);
      await ctx.sync();
      // Resize the chart to span widthColumns x heightRows starting at topLeft
      const widthCellRange = ws.getRange(op.topLeft).getResizedRange(0, op.widthColumns - 1);
      const heightCellRange = ws.getRange(op.topLeft).getResizedRange(op.heightRows - 1, 0);
      widthCellRange.load('width');
      heightCellRange.load('height');
      await ctx.sync();
      chart.left = tl.left;
      chart.top = tl.top;
      chart.width = widthCellRange.width;
      chart.height = heightCellRange.height;
      if (op.title) {
        chart.title.text = op.title;
        chart.title.format.font.bold = true;
        chart.title.format.font.size = 12;
      }
      if (op.hideLegend) {
        chart.legend.visible = false;
      }
      if (op.hideAxes) {
        // Doughnut/pie don't have axes; guard the call.
        // Move 4 hotfix #11 — same deferred-error pattern as series colors:
        // sync inside the try/catch so Office.js validation failures are
        // caught here instead of aborting the plan at the per-op sync.
        try {
          chart.axes.valueAxis.visible = false;
          chart.axes.categoryAxis.visible = false;
          await ctx.sync();
        } catch { /* not all chart types expose axes */ }
      }
      if (op.doughnutHoleSize !== undefined && (op.chartType === 'Doughnut')) {
        // Office.js exposes doughnutHoleSize via untyped properties on some
        // versions; set via the chart format if available.
        try {
          (chart as unknown as { doughnutHoleSize?: number }).doughnutHoleSize = op.doughnutHoleSize;
          await ctx.sync();
        } catch { /* ignore if not supported */ }
      }
      if (op.seriesNames && op.seriesNames.length > 0) {
        chart.series.load('count');
        await ctx.sync();
        for (let i = 0; i < Math.min(chart.series.count, op.seriesNames.length); i++) {
          chart.series.getItemAt(i).name = op.seriesNames[i];
        }
      }
      // Per-series overrides — required for combo charts (mixed bar/line)
      // and for color-coding individual series.
      //
      // Move 4 hotfix #11 — Office.js restrictions on series.format.fill /
      // series.format.line / per-series chartType vary by chart type and
      // host build. Synchronous try/catch doesn't catch deferred validation
      // errors; they fire at the next ctx.sync() and abort the entire plan.
      //
      // Fix: wrap each mutation with its own try { mutate; sync; } catch.
      // Per-mutation syncs cost ~5-10ms each; total overhead for a 4-chart
      // dashboard is well under a second. The dashboard renders even when
      // some color/axis tweaks fail — those are aesthetic, not load-bearing.
      if (op.seriesOverrides && op.seriesOverrides.length > 0) {
        chart.series.load('count');
        await ctx.sync();
        for (const override of op.seriesOverrides) {
          if (override.index < 0 || override.index >= chart.series.count) continue;
          const series = chart.series.getItemAt(override.index);
          if (override.chartType) {
            try {
              (series as unknown as { chartType?: string }).chartType = override.chartType;
              await ctx.sync();
            } catch { /* per-series chartType not supported on this build */ }
          }
          if (override.axisGroup) {
            try {
              (series as unknown as { axisGroup?: string }).axisGroup =
                override.axisGroup === 'Secondary' ? 'Secondary' : 'Primary';
              await ctx.sync();
            } catch { /* axisGroup not applicable */ }
          }
          if (override.color) {
            try {
              series.format.fill.setSolidColor(override.color);
              await ctx.sync();
            } catch { /* fill not applicable on this chart type */ }
            try {
              series.format.line.color = override.color;
              await ctx.sync();
            } catch { /* line color not applicable */ }
          }
        }
      }
      return;
    }

    case 'add_sparkline': {
      // Move 4 hotfix #10 — production sideload test 2026-05-09 surfaced
      // Office.js InvalidArgument on add_sparkline when board_pl_monthly
      // tried to place 18 sparklines. Two bugs:
      //
      //   1. typeMap was passing 'line' / 'column' / 'stacked' (lowercase)
      //      to sparklineGroups.add(). Excel.SparklineType enum's accepted
      //      string values are PascalCase: 'Line' / 'Column' / 'Stacked'.
      //      Office.js rejects the lowercase form with InvalidArgument.
      //
      //   2. The deferred Office.js error was killing the entire 446-op
      //      plan execution. Sparklines are visual sugar, not load-bearing
      //      — when they fail the rest of the dashboard should still build.
      //
      // Fix: pass op.sparklineType directly (PlanOp already declares it as
      // 'Line' | 'Column' | 'Stacked'), wrap in try/catch + internal sync
      // so deferred errors are caught here and silently skipped.
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      const sg = (ws as unknown as {
        sparklineGroups?: {
          add: (data: Excel.Range, ref: Excel.Range, type: string) => unknown;
        };
      }).sparklineGroups;
      if (!sg) {
        // SparklineGroups not in this Office.js build — silently skip.
        return;
      }
      // Office.js Excel.SparklineType enum: line="Line", column="Column",
      // stacked="Stacked". The accepted argument is the PascalCase string
      // value, NOT the lowercase enum key.
      const typeArg: string = op.sparklineType;
      try {
        const dataRange = ws.getRange(op.dataRange);
        const locRange = ws.getRange(op.locationRange);
        sg.add(dataRange, locRange, typeArg);
        // Internal sync surfaces deferred Office.js errors HERE so they
        // can be caught and silently skipped instead of aborting the plan.
        await ctx.sync();
      } catch {
        // Office.js rejected the sparkline — most common cause is the host
        // Excel build not supporting sparklineGroups.add (older Excel for
        // Mac, certain WebView2 builds). Skip silently; the dashboard
        // tables and charts still render.
        return;
      }
      return;
    }

    case 'add_named_range': {
      // Move 4 hotfix #4 — Office.js errors are DEFERRED to ctx.sync(),
      // not thrown synchronously from .add(). The previous try/catch
      // pattern never fired because .add() returns immediately and queues
      // the operation; the actual collision error surfaces at the outer
      // sync at the end of the executePlan loop, by which time we can no
      // longer attribute it to this specific op.
      //
      // Correct pattern: probe FIRST, delete-if-exists, then add. One
      // sync inside this op makes it self-contained and idempotent.
      const existing = ctx.workbook.names.getItemOrNullObject(op.name);
      existing.load('name');
      await ctx.sync();
      if (!(existing as unknown as { isNullObject?: boolean }).isNullObject) {
        existing.delete();
        await ctx.sync();
      }
      ctx.workbook.names.add(op.name, op.refersTo, op.comment);
      await ctx.sync();
      return;
    }

    case 'add_table': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      // Same idempotency pattern as add_named_range: delete by name first.
      const existing = ctx.workbook.tables.getItemOrNullObject(op.name);
      existing.load('name');
      await ctx.sync();
      if (!(existing as unknown as { isNullObject?: boolean }).isNullObject) {
        existing.delete();
        await ctx.sync();
      }
      const range = ws.getRange(op.range);
      const table = ws.tables.add(range, op.hasHeaders !== false);
      table.name = op.name;
      if (op.style) {
        table.style = op.style;
      }
      await ctx.sync();
      return;
    }

    case 'add_slicer': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      // Find source table
      const wb = ctx.workbook;
      const source = wb.tables.getItemOrNullObject(op.tableName);
      source.load('name');
      await ctx.sync();
      if ((source as unknown as { isNullObject?: boolean }).isNullObject) {
        throw new Error(`add_slicer: source table '${op.tableName}' not found (add it via add_table first)`);
      }
      const slicersAny = (ws as unknown as {
        slicers?: { add: (src: unknown, field: string) => unknown };
      }).slicers;
      if (!slicersAny) {
        // Slicers not supported in this Office.js build — skip silently.
        // The data_validation_list dropdown is the documented fallback.
        return;
      }
      let slicer: any;
      try {
        slicer = slicersAny.add(source, op.field);
      } catch (e) {
        throw new Error(`add_slicer failed: ${(e as Error).message}`);
      }
      // Position the slicer
      try {
        const tl = ws.getRange(op.topLeft);
        tl.load(['left', 'top']);
        const widthCellRange = ws.getRange(op.topLeft).getResizedRange(0, op.widthColumns - 1);
        const heightCellRange = ws.getRange(op.topLeft).getResizedRange(op.heightRows - 1, 0);
        widthCellRange.load('width');
        heightCellRange.load('height');
        await ctx.sync();
        if (slicer && typeof slicer === 'object') {
          (slicer as { left?: number; top?: number; width?: number; height?: number; caption?: string }).left = tl.left;
          (slicer as { left?: number; top?: number; width?: number; height?: number; caption?: string }).top = tl.top;
          (slicer as { left?: number; top?: number; width?: number; height?: number; caption?: string }).width = widthCellRange.width;
          (slicer as { left?: number; top?: number; width?: number; height?: number; caption?: string }).height = heightCellRange.height;
          if (op.caption) {
            (slicer as { caption?: string }).caption = op.caption;
          }
        }
      } catch { /* positioning is best-effort */ }
      return;
    }

    case 'add_text_box': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      const tl = ws.getRange(op.topLeft);
      tl.load(['left', 'top']);
      const widthCellRange = ws.getRange(op.topLeft).getResizedRange(0, op.widthColumns - 1);
      const heightCellRange = ws.getRange(op.topLeft).getResizedRange(op.heightRows - 1, 0);
      widthCellRange.load('width');
      heightCellRange.load('height');
      await ctx.sync();
      const shape = ws.shapes.addTextBox(op.text);
      shape.left = tl.left;
      shape.top = tl.top;
      shape.width = widthCellRange.width;
      shape.height = heightCellRange.height;
      try {
        const tf = shape.textFrame;
        const range = tf.textRange;
        if (op.fontColor) range.font.color = op.fontColor;
        if (op.fontBold !== undefined) range.font.bold = op.fontBold;
        if (op.fontSize !== undefined) range.font.size = op.fontSize;
        if (op.horizontalAlignment) {
          const align: Record<string, Excel.ShapeTextHorizontalAlignment> = {
            Left: Excel.ShapeTextHorizontalAlignment.left,
            Center: Excel.ShapeTextHorizontalAlignment.center,
            Right: Excel.ShapeTextHorizontalAlignment.right,
          };
          tf.horizontalAlignment = align[op.horizontalAlignment];
        }
        if (op.verticalAlignment) {
          const valign: Record<string, Excel.ShapeTextVerticalAlignment> = {
            Top: Excel.ShapeTextVerticalAlignment.top,
            Center: Excel.ShapeTextVerticalAlignment.middle,
            Bottom: Excel.ShapeTextVerticalAlignment.bottom,
          };
          tf.verticalAlignment = valign[op.verticalAlignment];
        }
      } catch { /* text-frame styling best-effort */ }
      if (op.fill) {
        try {
          shape.fill.setSolidColor(op.fill);
        } catch { /* ignore */ }
      }
      return;
    }

    case 'set_row_heights': {
      // Move 4 hotfix #16 — graceful skip pattern. Row heights are
      // best-effort visual sugar; if Office.js rejects, skip silently.
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      // Validate FIRST — out-of-range height is a real bug, not host quirk
      for (const r of op.heights) {
        if (r.height < 5 || r.height > 409) {
          const wrap = new Error(
            `set_row_heights: height ${r.height} for row ${r.row} is out of range [5, 409]`,
          );
          (wrap as unknown as { _errorCode: ErrorCode })._errorCode = 'invalid_width';
          throw wrap;
        }
      }
      try {
        for (const r of op.heights) {
          ws.getRange(`${r.row}:${r.row}`).format.rowHeight = r.height;
        }
        await ctx.sync();
      } catch { /* row height not applicable on this host — skip */ }
      return;
    }

    case 'freeze_panes': {
      // Move 4 hotfix #16 — Office.js sometimes rejects freezePanes on
      // certain Excel hosts (Excel Online, certain WebView2 contexts) with
      // a deferred error that aborts the entire plan. Freeze panes is UX
      // sugar — without it the user can still see the data, they just
      // have to scroll. Apply silent skip pattern: internal sync catches
      // deferred errors so the rest of the plan continues.
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      try {
        ws.freezePanes.unfreeze();
        if (op.rows && op.rows > 0) {
          ws.freezePanes.freezeRows(op.rows);
        }
        if (op.columns && op.columns > 0) {
          ws.freezePanes.freezeColumns(op.columns);
        }
        await ctx.sync();
      } catch { /* freeze panes not supported on this Excel host — skip */ }
      return;
    }

    case 'set_column_widths': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      // Move 4 Sprint 1 — validate widths up front. Office.js silently
      // clamps invalid widths, leaving cells rendering as ### with no
      // signal back to the agent. Reject early with a clear message.
      const bad = op.widths.filter(
        w => typeof w.width !== 'number' || isNaN(w.width) || w.width < COL_WIDTH_MIN || w.width > COL_WIDTH_MAX,
      );
      if (bad.length > 0) {
        const first = bad[0];
        const wrap = new Error(
          `set_column_widths: width ${first.width} for col ${first.col} is out of range [${COL_WIDTH_MIN}, ${COL_WIDTH_MAX}] (${bad.length} bad width(s) total)`,
        );
        (wrap as unknown as { _errorCode: ErrorCode })._errorCode = 'invalid_width';
        throw wrap;
      }
      for (const w of op.widths) {
        const colIdx = colSpecToIndex1(w.col);
        const colLetter = colNumToLetter(colIdx);
        ws.getRange(`${colLetter}:${colLetter}`).format.columnWidth = w.width;
      }
      return;
    }

    case 'data_validation_list': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      const range = ws.getRange(op.range);
      range.dataValidation.rule = {
        list: { inCellDropDown: true, source: op.source },
      };
      return;
    }

    case 'activate_sheet': {
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      ws.activate();
      return;
    }

    case 'add_page_break': {
      // Move 4 hotfix #8 + #16 — Insert horizontal page break before
      // `op.row` so charts print on a separate page. Now with internal
      // sync inside try/catch so deferred errors fire here, not at the
      // outer per-op sync which would abort the plan.
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      try {
        const breakAddr = `A${op.row}`;
        const wsAny = ws as unknown as {
          horizontalPageBreaks?: { add: (range: Excel.Range) => unknown };
        };
        if (wsAny.horizontalPageBreaks?.add) {
          wsAny.horizontalPageBreaks.add(ws.getRange(breakAddr));
          await ctx.sync();
        }
      } catch { /* page-break API unavailable on this host — non-fatal */ }
      return;
    }

    case 'set_print_area': {
      // Move 4 hotfix #8 + #16 — Each individual pageLayout mutation gets
      // its own try/catch + sync so partial success is fine. If
      // orientation can't be set on this host, skip it but still try
      // fitToPages, etc.
      const ws = await getSheet(ctx, op.sheet, false);
      if (!ws) throw new Error(`Sheet not found: ${op.sheet}`);
      try {
        const setPrintAreaFn = (ws.pageLayout as unknown as { setPrintArea?: (r: Excel.Range) => unknown }).setPrintArea;
        if (typeof setPrintAreaFn === 'function') {
          setPrintAreaFn.call(ws.pageLayout, ws.getRange(op.range));
          await ctx.sync();
        }
      } catch { /* setPrintArea not supported — skip */ }
      try {
        if (op.orientation) {
          (ws.pageLayout as unknown as { orientation?: string }).orientation =
            op.orientation === 'Landscape' ? Excel.PageOrientation.landscape as unknown as string : Excel.PageOrientation.portrait as unknown as string;
          await ctx.sync();
        }
      } catch { /* orientation not supported — skip */ }
      try {
        if (op.centerHorizontally !== undefined) {
          (ws.pageLayout as unknown as { centerHorizontally?: boolean }).centerHorizontally = op.centerHorizontally;
          await ctx.sync();
        }
      } catch { /* centering not supported — skip */ }
      try {
        if (op.fitToPagesWide !== undefined || op.fitToPagesTall !== undefined) {
          const z = (ws.pageLayout as unknown as { zoom?: { horizontalFitToPages?: number; verticalFitToPages?: number } }).zoom;
          if (z) {
            if (op.fitToPagesWide !== undefined) z.horizontalFitToPages = op.fitToPagesWide;
            if (op.fitToPagesTall !== undefined) z.verticalFitToPages = op.fitToPagesTall;
          }
          await ctx.sync();
        }
      } catch { /* fit-to-pages not supported — skip */ }
      return;
    }

    default: {
      const exhaustive: never = op;
      throw new Error(`Unknown op type: ${(exhaustive as { op: string }).op}`);
    }
  }
}

// ----- Move 4 Sprint 1 — post-render validation -----

interface WrittenRange {
  sheet: string;
  startCell: string;
  rows: number;
  cols: number;
}

/**
 * Walk the plan and collect every range that was *written to* (values,
 * formulas, formatting). These are the ranges where ### truncation could
 * appear. We only validate cells the plan actually touched — third-party
 * sheets and untouched cells are not the engine's responsibility.
 */
function collectWrittenRanges(plan: PlanOp[]): WrittenRange[] {
  const out: WrittenRange[] = [];
  for (const op of plan) {
    if (op.op === 'write_values') {
      const rows = op.values.length;
      const cols = rows > 0 ? op.values[0].length : 0;
      if (rows > 0 && cols > 0) out.push({ sheet: op.sheet, startCell: op.startCell, rows, cols });
    } else if (op.op === 'write_formulas_template') {
      out.push({ sheet: op.sheet, startCell: op.startCell, rows: op.rows, cols: op.cols });
    }
  }
  return out;
}

function countChartsExpected(plan: PlanOp[]): number {
  return plan.filter(op => op.op === 'add_chart').length;
}

function countChartsExpectedBySheet(plan: PlanOp[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const op of plan) {
    if (op.op === 'add_chart') {
      m.set(op.sheet, (m.get(op.sheet) ?? 0) + 1);
    }
  }
  return m;
}

/** Move 5 Step 3 — collect the names of every add_named_range op. */
function collectExpectedNamedRanges(plan: PlanOp[]): string[] {
  const names: string[] = [];
  for (const op of plan) {
    if (op.op === 'add_named_range') names.push(op.name);
  }
  return names;
}

/**
 * Move 5 Step 3 — formula error tokens Excel renders when refs/names/etc.
 * fail to resolve. We look for these in the rendered text of every written
 * range — a cheap proxy for "every formula resolved" without parsing
 * formula source.
 */
const FORMULA_ERROR_RE = /^#(REF|NAME|DIV\/0|N\/A|VALUE|NULL|NUM)(\?|!)?$/;

function detectFormulaErrorToken(cell: unknown): string | null {
  if (typeof cell !== 'string') return null;
  const trimmed = cell.trim();
  if (!trimmed) return null;
  // Excel renders these as "#REF!", "#NAME?", "#DIV/0!", "#N/A", "#VALUE!",
  // "#NULL!", "#NUM!" — match anchored to avoid catching legit "#1" entries.
  return FORMULA_ERROR_RE.test(trimmed) ? trimmed : null;
}

/**
 * After plan execution, scan written ranges for "###" patterns indicating
 * column-too-narrow truncation. If found, autofit those columns and re-scan
 * once. Issues that survive auto-fix are reported in the validation report.
 *
 * Also verifies chart counts per sheet.
 */
async function runValidationPass(
  ctx: Excel.RequestContext,
  plan: PlanOp[],
): Promise<ValidationReport> {
  const issues: ValidationIssue[] = [];
  let cellsScanned = 0;
  let columnsWidened = 0;
  let formulaErrorsFound = 0;       // Move 5 Step 3

  const written = collectWrittenRanges(plan);

  // Group by sheet to minimise sync calls
  const bySheet = new Map<string, WrittenRange[]>();
  for (const w of written) {
    const arr = bySheet.get(w.sheet) ?? [];
    arr.push(w);
    bySheet.set(w.sheet, arr);
  }

  // Pass 1: detect ### truncation + Move 5 Step 3 formula-error tokens per
  // sheet, autofit offending columns. Both checks share the same `text`
  // load — no extra Excel.sync round-trip.
  for (const [sheetName, ranges] of bySheet) {
    const ws = ctx.workbook.worksheets.getItemOrNullObject(sheetName);
    ws.load('name');
    await ctx.sync();
    if ((ws as unknown as { isNullObject?: boolean }).isNullObject) continue;

    const truncatedColumns = new Set<string>();
    // Cap formula_error_cells issues at 8 per sheet to keep the report
    // legible — once a build is broken at the formula level the user only
    // needs a few exemplars, not a flood.
    const FORMULA_ERROR_REPORT_CAP_PER_SHEET = 8;
    let formulaErrorsReportedThisSheet = 0;

    for (const r of ranges) {
      const start = ws.getRange(r.startCell);
      const region = start.getResizedRange(r.rows - 1, r.cols - 1);
      region.load(['text', 'columnIndex', 'columnCount', 'rowIndex']);
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
      const text = region.text as unknown as string[][];
      const startCol = region.columnIndex + 1;
      const startRow = (region.rowIndex ?? 0) + 1;
      cellsScanned += r.rows * r.cols;
      for (let i = 0; i < text.length; i++) {
        for (let j = 0; j < text[i].length; j++) {
          const cell = text[i][j];
          // Excel renders too-narrow numeric cells as one or more "#" chars.
          // Use a regex that matches 2+ # in a row to avoid catching legit "#"
          // strings (e.g. "Issue #1" entered by the agent). Formula-error
          // tokens (#REF!, #NAME?, …) only have one # so they can't collide.
          if (typeof cell === 'string' && /#{2,}/.test(cell)) {
            const colLetter = colNumToLetter(startCol + j);
            truncatedColumns.add(colLetter);
          }
          // Move 5 Step 3 — detect formula-evaluation errors in written cells.
          // These mean a formula's references didn't resolve at evaluation time.
          const errToken = detectFormulaErrorToken(cell);
          if (errToken) {
            formulaErrorsFound++;
            if (formulaErrorsReportedThisSheet < FORMULA_ERROR_REPORT_CAP_PER_SHEET) {
              const colLetter = colNumToLetter(startCol + j);
              issues.push({
                type: 'formula_error_cells',
                sheet: sheetName,
                range: `${colLetter}${startRow + i}`,
                message: `Cell ${colLetter}${startRow + i} on ${sheetName} rendered ${errToken} — formula reference did not resolve.`,
                auto_fixed: false,
                errorToken: errToken,
              });
              formulaErrorsReportedThisSheet++;
            }
          }
        }
      }
    }

    // Auto-fix: widen offending columns. Use autofit then bump up if still too narrow.
    if (truncatedColumns.size > 0) {
      for (const colLetter of truncatedColumns) {
        ws.getRange(`${colLetter}:${colLetter}`).format.autofitColumns();
      }
      columnsWidened += truncatedColumns.size;
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
    }

    // Pass 2: re-scan after autofit. If ### survives, autofit didn't help —
    // the cell content is genuinely too long for any reasonable column.
    // Bump those columns to a fixed wide value and re-scan one more time.
    //
    // Move 5 Step 7 (Audit fix M5) — pre-existing bug: pass 2 used to load
    // only 'text' then read region.columnIndex / region.rowIndex, which
    // throws PropertyNotLoaded on a fresh range object. Caught silently by
    // the outer try/catch and reported as "validation pass itself threw".
    // Step 3's formula-error scan made this hot path more likely to fire
    // during real builds, so worth fixing here.
    for (const r of ranges) {
      const start = ws.getRange(r.startCell);
      const region = start.getResizedRange(r.rows - 1, r.cols - 1);
      region.load(['text', 'columnIndex', 'rowIndex']);
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
      const text = region.text as unknown as string[][];
      const startCol = region.columnIndex + 1;
      const stillTruncated = new Set<string>();
      for (let i = 0; i < text.length; i++) {
        for (let j = 0; j < text[i].length; j++) {
          const cell = text[i][j];
          if (typeof cell === 'string' && /#{2,}/.test(cell)) {
            const colLetter = colNumToLetter(startCol + j);
            stillTruncated.add(colLetter);
          }
        }
      }
      if (stillTruncated.size > 0) {
        for (const colLetter of stillTruncated) {
          ws.getRange(`${colLetter}:${colLetter}`).format.columnWidth = 180;
        }
        columnsWidened += stillTruncated.size;
        // eslint-disable-next-line no-await-in-loop
        await ctx.sync();
        // Final re-scan
        const after = start.getResizedRange(r.rows - 1, r.cols - 1);
        after.load('text');
        // eslint-disable-next-line no-await-in-loop
        await ctx.sync();
        const t2 = after.text as unknown as string[][];
        for (let i = 0; i < t2.length; i++) {
          for (let j = 0; j < t2[i].length; j++) {
            const cell = t2[i][j];
            if (typeof cell === 'string' && /#{2,}/.test(cell)) {
              const colLetter = colNumToLetter(startCol + j);
              issues.push({
                type: 'truncated_cells',
                sheet: sheetName,
                range: `${colLetter}${(region.rowIndex ?? 0) + 1 + i}`,
                message: `Cell still rendered as ### after auto-widen (content too long for any reasonable column width)`,
                auto_fixed: false,
              });
            }
          }
        }
      }
    }
  }

  // Verify chart counts per sheet
  const expectedBySheet = countChartsExpectedBySheet(plan);
  let chartsExpectedTotal = 0;
  let chartsFoundTotal = 0;
  for (const [sheetName, expected] of expectedBySheet) {
    chartsExpectedTotal += expected;
    const ws = ctx.workbook.worksheets.getItemOrNullObject(sheetName);
    ws.load('name');
    // eslint-disable-next-line no-await-in-loop
    await ctx.sync();
    if ((ws as unknown as { isNullObject?: boolean }).isNullObject) {
      issues.push({
        type: 'missing_chart',
        sheet: sheetName,
        message: `Sheet ${sheetName} expected ${expected} chart(s) but the sheet doesn't exist`,
        auto_fixed: false,
      });
      continue;
    }
    ws.charts.load('count');
    // eslint-disable-next-line no-await-in-loop
    await ctx.sync();
    const found = ws.charts.count;
    chartsFoundTotal += found;
    if (found < expected) {
      issues.push({
        type: 'missing_chart',
        sheet: sheetName,
        message: `Expected ${expected} chart(s) on ${sheetName}, found ${found}`,
        auto_fixed: false,
      });
    }
  }

  // Move 5 Step 3 — verify every add_named_range op produced a usable name.
  // Office.js sometimes silently no-ops (out-of-bounds refersTo, sheet name
  // with embedded apostrophe quirks). We probe by name through both the
  // workbook-scoped collection and any sheet-scoped names.
  const expectedNames = collectExpectedNamedRanges(plan);
  let namedFound = 0;
  if (expectedNames.length > 0) {
    const wbNames = ctx.workbook.names;
    wbNames.load('items/name');
    await ctx.sync();
    const wbNameSet = new Set(wbNames.items.map(n => n.name));
    for (const expected of expectedNames) {
      if (wbNameSet.has(expected)) {
        namedFound++;
      } else {
        issues.push({
          type: 'named_range_missing',
          message: `Named range "${expected}" expected by add_named_range op was not found in the workbook after execution.`,
          auto_fixed: false,
        });
      }
    }
  }

  return {
    passed: issues.length === 0,
    issues,
    cells_scanned: cellsScanned,
    columns_widened: columnsWidened,
    charts_expected: chartsExpectedTotal,
    charts_found: chartsFoundTotal,
    named_ranges_expected: expectedNames.length,
    named_ranges_found: namedFound,
    formula_errors_found: formulaErrorsFound,
  };
}

function classifyErrorCode(message: string, hint?: ErrorCode): ErrorCode {
  if (hint) return hint;
  const msg = message.toLowerCase();
  if (msg.includes('sheet not found')) return 'sheet_not_found';
  if (msg.includes('range') && (msg.includes('invalid') || msg.includes('cannot') || msg.includes('parse'))) {
    return 'invalid_range';
  }
  if (msg.includes('chart') && msg.includes('type')) return 'invalid_chart_type';
  if (msg.includes('out of range')) return 'invalid_width';
  if (msg.includes('unknown op type')) return 'unknown_op_type';
  return 'office_api_error';
}

/**
 * Extract Office.js debugInfo from a thrown error object.
 *
 * Office.js wraps API errors as instances of OfficeExtension.Error which
 * carry a `debugInfo` object with the actual error code, message, location,
 * and full statement. The plain `.message` property is often a generic
 * "There is an issue..." string; debugInfo has the real diagnosis.
 */
function extractOfficeDebug(e: unknown): OfficeDebugInfo | undefined {
  if (!e || typeof e !== 'object') return undefined;
  const err = e as { debugInfo?: { code?: string; errorLocation?: string; fullStatement?: string; message?: string } };
  if (!err.debugInfo) return undefined;
  const d = err.debugInfo;
  return {
    code: d.code,
    errorMessage: d.message,
    errorLocation: d.errorLocation,
    fullStatement: d.fullStatement,
  };
}

/**
 * Move 4 hotfix #6 — per-op sync.
 *
 * The previous design queued all 600+ ops, then synced once at the end of
 * the loop. Office.js errors are deferred to ctx.sync() — so any op that
 * threw at sync time would surface in the OUTER catch with no per-op
 * attribution; we'd lose track of which op caused the failure.
 *
 * Now we sync after EACH op. Costs ~5-10ms per op (so ~3-6 seconds extra
 * for a 600-op plan, vs the previous behaviour of one final sync). In
 * exchange we get exact per-op error attribution AND the Office.js
 * debugInfo block for each failure.
 *
 * Ops that already sync internally (e.g. add_named_range with its
 * existence-probe) are unaffected — extra syncs are no-ops.
 */
/** The add-in's standard font. Excel's modern default is Aptos Narrow; every
 *  sheet a scaffold creates is normalised to this so all output is consistent. */
const STANDARD_FONT = 'Calibri';

/**
 * Force STANDARD_FONT on the used range of every sheet this plan CREATED
 * (i.e. appears in a create_sheet op). Scoped to created sheets so we never
 * alter the font of the user's pre-existing data. Only the font NAME is set —
 * bold, size, fills, number formats are untouched.
 */
async function applyStandardFont(ctx: Excel.RequestContext, plan: PlanOp[]): Promise<void> {
  const created = [
    ...new Set(
      plan
        .filter((o) => o.op === 'create_sheet')
        .map((o) => (o as { name?: string }).name)
        .filter((n): n is string => typeof n === 'string' && n.length > 0),
    ),
  ];
  if (created.length === 0) return;
  const usedRanges = created.map((name) => {
    const used = ctx.workbook.worksheets.getItemOrNullObject(name).getUsedRangeOrNullObject();
    used.load('isNullObject');
    return used;
  });
  await ctx.sync();
  for (const used of usedRanges) {
    if (!used.isNullObject) used.format.font.name = STANDARD_FONT;
  }
}

export async function executePlan(plan: PlanOp[]): Promise<PlanExecutionResult> {
  const started = Date.now();
  let opsCompleted = 0;
  let validation: ValidationReport | undefined;
  let structuredError: PlanExecutionError | undefined;

  try {
    await Excel.run(async (ctx) => {
      for (let i = 0; i < plan.length; i++) {
        const op = plan[i];
        try {
          await execOp(ctx, op);
          // Per-op sync — surface deferred Office.js errors at THIS op
          // index, not at the end of the loop where attribution is lost.
          await ctx.sync();
          opsCompleted = i + 1;
        } catch (e) {
          const raw = e as Error & { _errorCode?: ErrorCode };
          const code = classifyErrorCode(raw.message ?? '', raw._errorCode);
          const officeDebug = extractOfficeDebug(e);
          // Build a richer message that includes the Office.js debug location
          // so the agent's tool_result text is immediately diagnostic.
          let combined = raw.message ?? 'plan op failed';
          if (officeDebug?.code || officeDebug?.errorMessage || officeDebug?.errorLocation) {
            combined += ` [Office.js: code=${officeDebug.code ?? '?'} loc=${officeDebug.errorLocation ?? '?'} msg=${officeDebug.errorMessage ?? '?'}]`;
          }
          structuredError = {
            op_index: i,
            op_type: op.op,
            op_payload: op,
            error_code: code,
            message: combined,
            office_debug: officeDebug,
          };
          // Sentinel to escape Excel.run; the structured error is already captured.
          throw new Error('PLAN_OP_FAILED');
        }
      }
      // Final defensive sync — should be a no-op since we synced per op
      await ctx.sync();
      // Standard font (2026-06-10) — Excel's modern default is Aptos Narrow;
      // we standardise every sheet WE create on Calibri. Scoped to sheets that
      // appear in a create_sheet op (scaffold output) so we never re-font the
      // user's own existing data. Only sets font.name → bold/size/fills intact.
      await applyStandardFont(ctx, plan);
      await ctx.sync();
      // Move 4 Sprint 1 — post-render validation pass. Inside the same
      // Excel.run so any auto-fixes (column widening) commit atomically.
      validation = await runValidationPass(ctx, plan);
      await ctx.sync();
    });
  } catch (e) {
    if (structuredError) {
      return {
        ok: false,
        opsCompleted,
        opsTotal: plan.length,
        durationMs: Date.now() - started,
        error: structuredError,
      };
    }
    // Outer error — happens when validation pass itself throws.
    const msg = (e as Error).message ?? 'plan failed';
    const officeDebug = extractOfficeDebug(e);
    let combined = msg;
    if (officeDebug?.code || officeDebug?.errorMessage || officeDebug?.errorLocation) {
      combined += ` [Office.js: code=${officeDebug.code ?? '?'} loc=${officeDebug.errorLocation ?? '?'} msg=${officeDebug.errorMessage ?? '?'}]`;
    }
    return {
      ok: false,
      opsCompleted,
      opsTotal: plan.length,
      durationMs: Date.now() - started,
      error: {
        op_index: opsCompleted,
        op_type: plan[opsCompleted]?.op ?? 'unknown',
        op_payload: plan[opsCompleted] ?? ({ op: 'unknown' } as unknown as PlanOp),
        error_code: 'unknown',
        message: combined,
        office_debug: officeDebug,
      },
    };
  }

  return {
    ok: true,
    opsCompleted: plan.length,
    opsTotal: plan.length,
    durationMs: Date.now() - started,
    validation,
  };
}
